import _isEmpty from 'lodash/isEmpty';
import { createAction, createReducer } from 'redux-act';

import { SravniOrganizationsSharedOrganizationType } from '../__generated__/news/Api';
import { IMenu, IOrganizationMenuQuery } from '../interfaces/menu';
import {
  IPartnersInfoResult,
  TErrorModel,
  TOrganizationData,
  TOrganizationsInfoQuery,
  TPartnersInfoQuery,
} from '../interfaces/news';
import { ExtendedNewsOrg, IOrgMeta } from '../interfaces/organizations';
import { getOrganizations, getPartners, selectOrganizationMenuByAlias } from '../selectors/organizations';
import { loadOrganizationMenu } from '../services/menu';
import { getOrganizationsInfo, getPartnersInfo } from '../services/news';
import { getOrgByAlias } from '../services/organizations';
import { App } from '../types';

export interface IState {
  list: {
    data: {
      [key: string]: ExtendedNewsOrg;
    };
    loading: boolean;
    error: TErrorModel;
  };
  partners: {
    data: IPartnersInfoResult;
    loading: boolean;
    error: TErrorModel;
  };
  menu: {
    data: {
      [key: string]: IMenu[];
    };
    loading: boolean;
    error: TErrorModel;
  };
}

const initialState: IState = {
  list: {
    data: {},
    loading: false,
    error: null,
  },
  partners: {
    data: {},
    loading: false,
    error: null,
  },
  menu: {
    data: {},
    loading: false,
    error: null,
  },
};

export const startLoadingOrganizationsInfo = createAction('organizations/list/loading');
export const loadOrganizationsInfoSuccess = createAction<{
  [key: string]: TOrganizationData;
}>('organizations/list/success');
export const loadOrganizationsInfoFail = createAction<TErrorModel>('organizations/list/fail');

export const startLoadingPartnersInfo = createAction('organizations/partners/loading');
export const loadPartnersInfoSuccess = createAction<IPartnersInfoResult>('organizations/partners/success');
export const loadPartnersInfoFail = createAction<TErrorModel>('organizations/partners/fail');

export const startLoadingMenu = createAction('organizations/menu/loading');
export const loadMenuSuccess = createAction<{ [key: string]: IMenu[] }>('organizations/menu/success');
export const loadMenuFail = createAction<TErrorModel>('organizations/menu/fail');

export const startLoadingOrg = createAction('organizations/org/loading');
export const loadOrgSuccess = createAction<IOrgMeta>('organizations/org/success');
export const loadOrgFail = createAction<TErrorModel>('organizations/org/fail');

export const fetchOrganizationByAlias = (params: {
  type: SravniOrganizationsSharedOrganizationType;
  alias: string;
}): App.ReduxAction => {
  return async (dispatch) => {
    try {
      dispatch(startLoadingOrg());
      const data = await getOrgByAlias(params);
      dispatch(loadOrgSuccess(data));
    } catch (error) {
      dispatch(loadOrgFail(error));
    }
  };
};

export const fetchOrganizationsInfoIfNeeded = (params?: TOrganizationsInfoQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const partnersInfo = getOrganizations(getState());
    if (_isEmpty(partnersInfo)) {
      try {
        dispatch(startLoadingOrganizationsInfo());
        const organizations = await getOrganizationsInfo(params);
        const data = organizations.reduce(
          (output, item) => ({
            ...output,
            [item.alias]: {
              ...item,
              id: item.id ?? item.mongoId ?? item.oldId,
            },
          }),
          {},
        ) as { [key: string]: TOrganizationData };
        dispatch(loadOrganizationsInfoSuccess(data));
      } catch (error) {
        dispatch(loadOrganizationsInfoFail(error));
      }
    }
  };
};

export const fetchPartnersInfoIfNeeded = (params?: TPartnersInfoQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const partnersInfo = getPartners(getState());
    if (_isEmpty(partnersInfo)) {
      try {
        dispatch(startLoadingPartnersInfo());
        const data = await getPartnersInfo(params);
        dispatch(loadPartnersInfoSuccess(data));
      } catch (error) {
        dispatch(loadPartnersInfoFail(error));
      }
    }
  };
};

export const fetchOrganizationMenuIfNeeded = (params?: IOrganizationMenuQuery): App.ReduxAction => {
  return async (dispatch, getState) => {
    const { alias } = params;

    const hasMenu = Boolean(selectOrganizationMenuByAlias(alias)(getState()));

    if (hasMenu) return;

    try {
      dispatch(startLoadingMenu());

      const { menu } = await loadOrganizationMenu(params);

      const data = {
        [alias]: menu,
      };
      dispatch(loadMenuSuccess(data));
    } catch (error) {
      dispatch(loadMenuFail(error));
    }
  };
};

const organizations = createReducer<IState>({}, initialState)
  .on(startLoadingOrganizationsInfo, (state) => {
    return {
      ...state,
      list: {
        ...state.list,
        loading: true,
      },
    };
  })
  .on(loadOrganizationsInfoSuccess, (state, payload) => {
    return {
      ...state,
      list: {
        ...state.list,
        data: payload,
        loading: false,
      },
    };
  })
  .on(loadOrganizationsInfoFail, (state, error) => {
    return {
      ...state,
      list: {
        ...state.list,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingPartnersInfo, (state) => {
    return {
      ...state,
      partners: {
        ...state.partners,
        loading: true,
      },
    };
  })
  .on(loadPartnersInfoSuccess, (state, payload) => {
    return {
      ...state,
      partners: {
        ...state.partners,
        data: payload,
        loading: false,
      },
    };
  })
  .on(loadPartnersInfoFail, (state, error) => {
    return {
      ...state,
      partners: {
        ...state.partners,
        error,
        loading: false,
      },
    };
  })

  .on(startLoadingMenu, (state) => {
    return {
      ...state,
      menu: {
        ...state.menu,
        loading: true,
      },
    };
  })
  .on(loadMenuSuccess, (state, payload) => {
    return {
      ...state,
      menu: {
        ...state.menu,
        data: payload,
        loading: false,
      },
    };
  })
  .on(loadMenuFail, (state, error) => {
    return {
      ...state,
      menu: {
        ...state.menu,
        error,
        loading: false,
      },
    };
  })
  //@ts-ignore
  .on(loadOrgSuccess, (state, payload) => {
    return {
      ...state,
      list: {
        ...state.list,
        data: {
          ...state.list.data,
          [`${payload.id}`]: {
            ...state.list.data[`${payload.id}`],
            ...payload,
          },
        },
        loading: false,
      },
    };
  });

export default organizations;
